/**
 * Utility functions for extracting metadata from media files
 */

/**
 * Get duration of audio or video file
 */
export function getMediaDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const media = document.createElement(
      file.type.startsWith('audio/') ? 'audio' : 'video'
    );
    
    media.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(url);
      const duration = Math.floor(media.duration);
      if (duration && duration > 0) {
        resolve(duration);
      } else {
        reject(new Error('Could not determine media duration'));
      }
    });
    
    media.addEventListener('error', (e) => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load media for duration extraction'));
    });
    
    media.src = url;
  });
}

/**
 * Format duration in seconds to MM:SS format
 */
export function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Generate thumbnail for audio files (returns a default thumbnail URL)
 * In the future, this could generate a waveform image
 */
export function getAudioThumbnailUrl(): string {
  // Return a default audio thumbnail
  // You can replace this with a custom image or waveform generation
  return '/audio-thumbnail.png';
}

/**
 * Determine media type from file
 */
export function getMediaType(file: File): 'image' | 'video' | 'audio' | 'document' {
  const mimeType = file.type;
  
  if (mimeType.startsWith('image/')) {
    return 'image';
  } else if (mimeType.startsWith('video/')) {
    return 'video';
  } else if (mimeType.startsWith('audio/')) {
    return 'audio';
  } else {
    return 'document';
  }
}
